/* ==========================================================================
   Hidecraft — global.js
   Vanilla JS + custom elements. No jQuery.
   ========================================================================== */

(() => {
  'use strict';

  /* ---------- Utilities ---------- */

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length
    );
  }

  const focusTrap = {
    container: null,
    handler: null,
    trap(container, elementToFocus) {
      this.release();
      this.container = container;
      this.handler = (event) => {
        if (event.key !== 'Tab') return;
        const items = getFocusable(container);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.handler);
      (elementToFocus || getFocusable(container)[0] || container).focus();
    },
    release(elementToFocus) {
      if (this.handler) document.removeEventListener('keydown', this.handler);
      this.handler = null;
      this.container = null;
      if (elementToFocus) elementToFocus.focus();
    },
  };

  function debounce(fn, wait = 150) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function lockScroll(lock) {
    document.body.classList.toggle('overflow-hidden', lock);
  }

  /* Shopify money format: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}} ... */
  function formatMoney(cents, format = window.theme?.settings?.moneyFormat || '{{amount}}') {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : Math.round(cents);
    const split = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return split(value, 0);
        case 'amount_with_comma_separator':
          return split(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return split(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return split(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return split(value, 0, ' ');
        case 'amount_with_space_separator':
          return split(value, 2, ' ', ',');
        default:
          return split(value, 2);
      }
    });
  }

  /* Fetch a page (or section) and return a parsed document. */
  async function fetchSection(url) {
    const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  }

  window.Hidecraft = { getFocusable, focusTrap, debounce, lockScroll, isDesktop, formatMoney, fetchSection, prefersReducedMotion };

  /* ---------- Announcement bar ---------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement-bar__message'));
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.storageKey = `hc-announcement-${this.dataset.sectionId}`;

      if (this.dataset.dismissible === 'true' && !window.Shopify?.designMode) {
        try {
          if (sessionStorage.getItem(this.storageKey) === 'closed') {
            this.closest('.shopify-section')?.setAttribute('hidden', '');
            return;
          }
        } catch (e) {
          /* storage blocked */
        }
      }

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.querySelector('[data-announcement-close]')?.addEventListener('click', () => this.close());

      if (this.slides.length > 1) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.play());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.play());
        this.play();
      }

      this.addEventListener('shopify:block:select', (event) => {
        this.stop();
        this.go(this.slides.indexOf(event.target));
      });
      this.addEventListener('shopify:block:deselect', () => this.play());
    }

    disconnectedCallback() {
      this.stop();
    }

    go(index, manual = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => (active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      if (manual) {
        this.stop();
        this.play();
      }
    }

    play() {
      if (this.slides.length < 2 || prefersReducedMotion() || this.dataset.autoplay !== 'true') return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
    }

    close() {
      this.stop();
      try {
        sessionStorage.setItem(this.storageKey, 'closed');
      } catch (e) {
        /* storage blocked */
      }
      this.closest('.shopify-section')?.setAttribute('hidden', '');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Countdown timer ---------- */

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = raw ? new Date(raw).getTime() : NaN;
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.parts = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = Math.max(0, this.end - Date.now());
      if (diff === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideOnEnd === 'true') {
          this.hidden = true;
        } else {
          this.innerHTML = `<span>${window.theme?.strings?.countdownEnded || ''}</span>`;
        }
        return;
      }
      const s = Math.floor(diff / 1000);
      const values = {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
      };
      Object.entries(values).forEach(([key, value]) => {
        if (this.parts[key]) this.parts[key].textContent = String(value).padStart(2, '0');
      });
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Sticky header ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.section = this.closest('.hc-site-header');
      if (!this.section) return;

      this.section.classList.toggle('hc-site-header--sticky', this.mode !== 'none');
      this.transparent = this.dataset.transparent === 'true';
      this.resizeObserver = new ResizeObserver(() =>
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`)
      );
      this.resizeObserver.observe(this);
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    onScroll() {
      const y = window.scrollY;
      const offset = this.section.offsetHeight;
      this.section.classList.toggle('is-scrolled', y > 10);
      if (this.transparent) this.classList.toggle('header--transparent-active', y <= 10);

      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > offset * 2 && !document.body.classList.contains('overflow-hidden');
        this.section.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop menu (dropdown + mega menu) ---------- */

  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(':scope details.header-menu__details'));
      this.items.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', String(details.open));

        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(details.open));
          if (details.open) this.closeOthers(details);
        });

        details.addEventListener('mouseenter', () => {
          if (!isDesktop() || this.dataset.trigger !== 'hover') return;
          clearTimeout(details._closeTimer);
          details.open = true;
        });
        details.addEventListener('mouseleave', () => {
          if (!isDesktop() || this.dataset.trigger !== 'hover') return;
          details._closeTimer = setTimeout(() => (details.open = false), 150);
        });
        details.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' && details.open) {
            details.open = false;
            summary.focus();
          }
        });
        details.addEventListener('focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      });

      this.onDocClick = (event) => {
        if (!this.contains(event.target)) this.closeOthers();
      };
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    closeOthers(except) {
      this.items.forEach((d) => {
        if (d !== except) d.open = false;
      });
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ---------- Drawer / modal (mobile menu, search) ---------- */

  class ModalDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');
      // Panels marked inert in markup are toggled with inert; others (e.g. desktop filter sidebar) rely on CSS.
      this.useInert = Boolean(this.panel?.hasAttribute('inert'));

      // Delegated so openers rendered later (Section Rendering API) still work.
      this.onDocumentClick = (event) => {
        const opener = event.target.closest(`[aria-controls="${this.id}"]`);
        if (!opener || this.contains(opener)) return;
        event.preventDefault();
        this.open(opener);
      };
      document.addEventListener('click', this.onDocumentClick);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.overlay?.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get openers() {
      return document.querySelectorAll(`[aria-controls="${this.id}"]`);
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      if (this.useInert) this.panel?.removeAttribute('inert');
      this.overlay?.classList.add('is-active');
      this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      lockScroll(true);
      requestAnimationFrame(() => {
        const autofocus = this.querySelector('[autofocus], input[type="search"]');
        focusTrap.trap(this.panel || this, autofocus);
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      if (this.useInert) this.panel?.setAttribute('inert', '');
      this.overlay?.classList.remove('is-active');
      this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      lockScroll(false);
      focusTrap.release(this.opener?.isConnected ? this.opener : null);
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  customElements.define('modal-drawer', ModalDrawer);
  window.Hidecraft.ModalDrawer = ModalDrawer;

  /* ---------- Slider (carousels, slideshow, testimonials) ---------- */

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.playButton = this.querySelector('[data-slider-play]');
      this.autoplay = this.dataset.autoplay === 'true' && !prefersReducedMotion();
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.rtl = document.documentElement.dir === 'rtl';

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.playButton?.addEventListener('click', () => this.togglePlay());

      this.onScroll = debounce(() => this.update(), 60);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      if (this.autoplay) {
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.resume());
        this.play();
      }

      this.addEventListener('shopify:block:select', (event) => {
        this.pause();
        const i = this.slides.indexOf(event.target.closest('[data-slider-track] > *'));
        if (i > -1) this.goTo(i, 'auto');
      });
      this.addEventListener('shopify:block:deselect', () => this.resume());

      this.update();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
    }

    get slideWidth() {
      const first = this.slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    get currentIndex() {
      const w = this.slideWidth;
      return w ? Math.round(Math.abs(this.track.scrollLeft) / w) : 0;
    }

    get maxIndex() {
      const w = this.slideWidth;
      if (!w) return 0;
      const visible = Math.max(1, Math.round(this.track.clientWidth / w));
      return Math.max(0, this.slides.length - visible);
    }

    goTo(index, behavior = 'smooth') {
      const max = this.maxIndex;
      let i = index;
      if (i > max) i = this.dataset.loop === 'true' ? 0 : max;
      if (i < 0) i = this.dataset.loop === 'true' ? max : 0;
      const left = i * this.slideWidth * (this.rtl ? -1 : 1);
      this.track.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : behavior });
    }

    step(dir) {
      this.goTo(this.currentIndex + dir);
    }

    update() {
      const i = this.currentIndex;
      const max = this.maxIndex;
      const loop = this.dataset.loop === 'true';
      if (this.prev) this.prev.disabled = !loop && i <= 0;
      if (this.next) this.next.disabled = !loop && i >= max;
      this.classList.toggle('slider--static', max === 0);
      this.dots.forEach((dot, d) => dot.setAttribute('aria-current', String(d === Math.min(i, this.dots.length - 1))));
      this.slides.forEach((slide, s) => {
        const visible = slide.getBoundingClientRect();
        const box = this.track.getBoundingClientRect();
        const inView = visible.left >= box.left - 2 && visible.right <= box.right + 2;
        slide.toggleAttribute('inert', !inView && this.dataset.inertHidden === 'true');
        slide.classList.toggle('is-active', s === i);
      });
      if (i !== this.lastIndex) {
        this.lastIndex = i;
        this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { index: i, slide: this.slides[i] } }));
      }
    }

    /* Re-read slides after items are hidden/shown (e.g. variant image filtering). */
    refresh() {
      this.slides = Array.from(this.track.children).filter((el) => !el.hidden);
      this.update();
    }

    play() {
      clearInterval(this.timer);
      this.playing = true;
      this.timer = setInterval(() => this.goTo(this.currentIndex >= this.maxIndex ? 0 : this.currentIndex + 1), this.speed);
      this.playButton?.setAttribute('aria-pressed', 'false');
    }

    pause() {
      clearInterval(this.timer);
    }

    resume() {
      if (this.autoplay && this.playing) this.play();
    }

    togglePlay() {
      if (this.playing) {
        this.playing = false;
        this.pause();
        this.playButton?.setAttribute('aria-pressed', 'true');
      } else {
        this.autoplay = true;
        this.play();
      }
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Tabs (product tabs section) ---------- */

  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
          if (!(event.key in keys)) return;
          event.preventDefault();
          let next = keys[event.key];
          if (next === 'first') next = 0;
          else if (next === 'last') next = this.tabs.length - 1;
          else next = (i + next * (document.documentElement.dir === 'rtl' ? -1 : 1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.panels.indexOf(event.target.closest('[role="tabpanel"]'));
        const t = this.tabs.indexOf(event.target.closest('[role="tab"]'));
        if (i > -1) this.select(i);
        else if (t > -1) this.select(t);
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, i) => (panel.hidden = i !== index));
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------- Localization form ---------- */

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('select').forEach((select) =>
        select.addEventListener('change', () => this.querySelector('form')?.submit())
      );
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* ---------- Back to top ---------- */

  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        document.getElementById('MainContent')?.focus({ preventScroll: true });
      });
      this.onScroll = () => this.classList.toggle('is-visible', window.scrollY > window.innerHeight);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('back-to-top', BackToTop);

  /* ---------- Quick add (card forms) ----------
     cart.js (window.Hidecraft.cart) handles the AJAX add + drawer/notification.
     Without it, or with cart type 'page', the form posts normally to /cart/add. */

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form || !window.Hidecraft.cart || window.theme.settings.cartType === 'page') return;
    event.preventDefault();
    window.Hidecraft.cart.addFromForm(form);
  });

  /* ---------- Product recommendations (related, complementary, cart upsell) ---------- */

  class ProductRecommendations extends HTMLElement {
    static cache = new Map();

    connectedCallback() {
      if (!this.dataset.url) return;
      if ('IntersectionObserver' in window && this.dataset.eager !== 'true') {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        observer.observe(this);
      } else {
        this.load();
      }
    }

    async load() {
      const url = this.dataset.url;
      try {
        let html = ProductRecommendations.cache.get(url);
        if (!html) {
          const doc = await fetchSection(url);
          const inner = doc.querySelector('product-recommendations');
          html = inner ? inner.innerHTML : '';
          ProductRecommendations.cache.set(url, html);
        }
        if (html.trim()) {
          this.innerHTML = html;
          this.classList.add('is-loaded');
          this.closest('[data-recommendations-wrapper]')?.removeAttribute('hidden');
        } else if (this.dataset.removeEmpty === 'true') {
          this.closest('[data-recommendations-wrapper]')?.setAttribute('hidden', '');
        }
      } catch (error) {
        console.warn(error);
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Reveal on scroll ---------- */

  function initReveal(root = document) {
    if (!document.body.dataset.animation) return;
    const els = root.querySelectorAll ? root.querySelectorAll('.reveal:not(.is-revealed)') : [];
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    els.forEach((el) => observer.observe(el));
  }

  /* ---------- Init ---------- */

  function init() {
    initReveal();
    // Content added later (tabs, filters, recommendations, AJAX) must also become visible
    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) =>
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.classList.contains('reveal') && !node.classList.contains('is-revealed')) {
              initReveal(node.parentElement || document);
            } else if (node.querySelector('.reveal:not(.is-revealed)')) {
              initReveal(node);
            }
          })
        );
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
  });
})();
